// src/pages/AdminApproveBatch.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../utils/config";

export default function AdminApproveBatch() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");

  const [batch, setBatch] = useState(null);
  const [allocations, setAllocations] = useState({});
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Keep this for server-side / submit errors
  const [error, setError] = useState("");

  // NEW: per-item validation errors shown on the page
  const [itemErrors, setItemErrors] = useState({}); // { [batchItemId]: "msg" }

  // ---------- LOAD BATCH ----------
  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(
          `${API_BASE_URL}/api/admin/borrow-batches/${batchId}/approval/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("Failed to load batch.");
        const data = await res.json();
        setBatch(data);

        // init allocations
        const init = {};
        data.items.forEach((it) => {
          init[it.id] = [
            {
              location_id: it.location_stocks[0]?.location_id || null,
              quantity: it.quantity,
            },
          ];
        });
        setAllocations(init);
      } catch (e) {
        console.error(e);
        setError("Failed to load approval data.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [batchId, token]);

  // ---------- HELPERS ----------
  const allocatedSum = (batchItemId) =>
    (allocations[batchItemId] || []).reduce(
      (s, a) => s + (Number(a.quantity) || 0),
      0
    );

  const updateAllocation = (batchItemId, idx, field, value) => {
    setAllocations((prev) => {
      const rows = [...(prev[batchItemId] || [])];
      rows[idx] = { ...rows[idx], [field]: value };
      return { ...prev, [batchItemId]: rows };
    });
  };

  const addRow = (batchItemId, requestedQty) => {
    setAllocations((prev) => {
      const existing = prev[batchItemId] || [];
      const allocated = existing.reduce(
        (s, a) => s + (Number(a.quantity) || 0),
        0
      );

      const remaining = Math.max(requestedQty - allocated, 0);

      return {
        ...prev,
        [batchItemId]: [
          ...existing,
          {
            location_id: null,
            quantity: remaining > 0 ? remaining : "",
          },
        ],
      };
    });
  };

  const removeRow = (batchItemId, idx) => {
    setAllocations((prev) => {
      const rows = [...prev[batchItemId]];
      rows.splice(idx, 1);
      return { ...prev, [batchItemId]: rows };
    });
  };

  const validateAll = () => {
    const perItemErrors = {};

    // note must exist
    if (!note.trim()) {
      // don't set a global error banner; just block the button
      // (UX: user already sees "Approval note *")
    }

    if (!batch?.items?.length) {
      return { ok: false, perItemErrors: {} };
    }

    for (const it of batch.items) {
      const rows = allocations[it.id] || [];

      if (!rows.length) {
        perItemErrors[it.id] = `${it.item_code}: please allocate at least one gudang`;
        continue;
      }

      // sum must match requested
      const sum = allocatedSum(it.id);
      if (sum !== it.quantity) {
        perItemErrors[it.id] = `${it.item_code}: requested ${it.quantity}, allocated ${sum}`;
        continue;
      }

      // location must be unique, qty must be > 0, and must not exceed available
      const locs = new Set();
      for (const r of rows) {
        const locId = r.location_id;
        const qty = Number(r.quantity) || 0;

        if (!locId) {
          perItemErrors[it.id] = `${it.item_code}: please select gudang for all rows`;
          break;
        }

        if (locs.has(locId)) {
          perItemErrors[it.id] = `${it.item_code}: duplicate gudang selected`;
          break;
        }

        if (qty <= 0) {
          perItemErrors[it.id] = `${it.item_code}: quantity must be > 0`;
          break;
        }

        const ls = it.location_stocks.find((s) => s.location_id === locId);
        const available = ls?.available ?? 0;

        if (qty > available) {
          perItemErrors[it.id] = `${it.item_code}: requested ${qty}, but only ${available} available in this location`;
          break;
        }

        locs.add(locId);
      }
    }

    const itemsOk = Object.keys(perItemErrors).length === 0;
    const noteOk = !!note.trim();

    return { ok: itemsOk && noteOk, perItemErrors };
  };

  // ---------- LIVE VALIDATION (runs on page, not on submit) ----------
  const { canApprove, computedItemErrors } = useMemo(() => {
    const { ok, perItemErrors } = validateAll();
    return { canApprove: ok, computedItemErrors: perItemErrors };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch, allocations, note]);

  useEffect(() => {
    setItemErrors(computedItemErrors);
  }, [computedItemErrors]);

  // ---------- SUBMIT ----------
  const submit = async () => {
    setError("");

    // block submit if invalid (so no surprise redirects / empty page)
    if (!canApprove) return;
    // block double-submit (double-click / two tabs racing the same approval)
    if (submitting) return;
    setSubmitting(true);

    try {
      const payload = {
        note,
        items: batch.items.map((it) => ({
          batch_item_id: it.id,
          allocations: allocations[it.id].map((a) => ({
            location_id: a.location_id,
            quantity: Number(a.quantity),
          })),
        })),
      };

      const res = await fetch(
        `${API_BASE_URL}/api/admin/borrow-batches/${batchId}/approve/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const d = await res.json().catch(() => ({}));

        // DRF often returns {field: ["msg"]} not {detail: "..." }
        const firstFieldError =
          d && typeof d === "object"
            ? Object.values(d).flat?.()[0]
            : null;

        throw new Error(d.detail || firstFieldError || "Approval failed.");
      }

      navigate("/admin/borrow-batches");
    } catch (e) {
      console.error(e);
      setError(e.message || "Approval failed.");
      setSubmitting(false);
    }
  };

  // ---------- RENDER ----------
  if (loading) return <p className="text-sm text-muted">Loading…</p>;

  if (!batch) return null;

  if (batch.status !== "PENDING") {
    navigate("/admin/borrow-batches");
    return null;
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-2">
        Approve Batch {batch.reference_code}
      </h1>

      <div className="text-xs text-muted mb-4">
        Borrower: <b>{batch.user}</b> · Expected return:{" "}
        <b>{batch.expected_return_date}</b>
      </div>

      {/* TOP ERROR BANNER (server-side submit errors) */}
      {error && (
        <div className="text-sm text-red-600 mb-4">
          {error}
        </div>
      )}

      {/* ITEMS */}
      {batch.items.map((it) => {
        const allocated = allocatedSum(it.id);
        const hasItemError = !!itemErrors[it.id];

        return (
          <div
            key={it.id}
            className={`mb-4 border rounded bg-white/80 dark:bg-white/5 p-4 ${
              hasItemError ? "border-red-300" : "border-white/40 dark:border-white/10"
            }`}
          >
            <div className="flex justify-between mb-2">
              <div className="font-semibold text-sm">
                {it.item_code} — {it.item_name}
              </div>
              <div
                className={`text-xs ${
                  allocated === it.quantity ? "text-green-600" : "text-red-600"
                }`}
              >
                {allocated}/{it.quantity}
              </div>
            </div>

            {/* NEW: per-item validation message */}
            {hasItemError && (
              <div className="text-xs text-red-600 mb-2">
                {itemErrors[it.id]}
              </div>
            )}

            {(allocations[it.id] || []).map((row, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <select
                  value={row.location_id || ""}
                  onChange={(e) =>
                    updateAllocation(
                      it.id,
                      idx,
                      "location_id",
                      Number(e.target.value)
                    )
                  }
                  className="border border-white/50 dark:border-white/15 rounded px-2 py-1 text-xs"
                >
                  <option value="">Select gudang</option>
                  {it.location_stocks.map((ls) => (
                    <option
                      key={ls.location_id}
                      value={ls.location_id}
                      disabled={ls.available === 0}
                    >
                      {ls.location_name} ({ls.available})
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  min={1}
                  value={row.quantity}
                  onChange={(e) =>
                    updateAllocation(it.id, idx, "quantity", e.target.value)
                  }
                  className="w-20 border border-white/50 dark:border-white/15 rounded px-2 py-1 text-xs"
                />

                {allocations[it.id]?.length > 1 && (
                  <button
                    onClick={() => removeRow(it.id, idx)}
                    className="text-xs text-red-600"
                    type="button"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={() => addRow(it.id, it.quantity)}
              className="text-xs text-brand-700 font-semibold"
              type="button"
            >
              + Add gudang
            </button>
          </div>
        );
      })}

      {/* NOTE */}
      <div className="mt-6 max-w-xl">
        <label className="block text-xs font-semibold mb-1">
          Approval note *
        </label>
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full border border-white/50 dark:border-white/15 rounded px-3 py-2 text-sm"
          placeholder="Enter approval note here..."
        />
      </div>

      <div className="flex gap-2 mt-4">
        <button
          disabled={!canApprove || submitting}
          onClick={submit}
          className={`px-4 py-2 text-white text-sm rounded disabled:opacity-50 ${
            canApprove ? "bg-green-600" : "bg-green-600"
          }`}
          type="button"
        >
          {submitting ? "Approving…" : "Approve & Deduct Stock"}
        </button>

        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 border border-white/50 dark:border-white/15 rounded text-sm"
          type="button"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
